import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { session } from '@/lib/api/session';
import { settingsService, type BrandingSettings } from '@/lib/api/services/settings.service';

const DEFAULT_FAVICON = '/xyra.webp';

/** Swaps the browser tab icon — reverts to the default the moment branding clears (tenant switch, sign-out). */
function applyFavicon(url: string | undefined) {
  const link = document.querySelector<HTMLLinkElement>('#favicon');
  if (link) link.href = url || DEFAULT_FAVICON;
}

/**
 * Pre-auth white-label branding, keyed by the tenant slug — the same lookup
 * works before login (AuthShell/LoginPage, using the last-typed slug that
 * deliberately survives sign-out, see session.ts) and after login (Sidebar,
 * using the same slug). A tenant is "branded" once it has set a company name
 * or a logo; unbranded tenants render every surface exactly as before this
 * feature existed — no behavior change.
 */
export function useBranding(slugOverride?: string) {
  const slug = slugOverride || session.tenantSlug || '';
  const query = useQuery({
    queryKey: ['branding', slug],
    queryFn: () => settingsService.publicBranding(slug),
    enabled: !!slug,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const branding: BrandingSettings | undefined = query.data?.groups.branding;
  const isBranded = !!(branding?.companyName || branding?.logoUrl);

  useEffect(() => {
    applyFavicon(branding?.faviconUrl);
  }, [branding?.faviconUrl]);

  return { branding, isBranded, isLoading: query.isLoading };
}
